import * as React from 'react';

import { Checkbox } from '@ao-internal/components-react';

export type CheckboxScoreValue = {
	label: React.ReactNode;
	ariaLabel?: string;
	value: string;
	isChecked: boolean;
};

export interface CheckboxScoreButtonsProps {
	name: string;
	values: CheckboxScoreValue[];
	label: string;
	boundaryLabels?: {
		start: string;
		end: string;
	};
	handleChange: (evt: React.ChangeEvent<HTMLInputElement>) => void;
	radioChoice?: boolean; // Behave like a radio button
}

export const CheckboxScoreButtons: React.FC<
	CheckboxScoreButtonsProps & React.HTMLProps<HTMLFieldSetElement>
> = ({
	name,
	values,
	label,
	boundaryLabels,
	handleChange,
	radioChoice,
	...props
}) => {
	const checkboxRefs = React.useRef<HTMLInputElement[]>([]);
	const checkboxStates = React.useRef<CheckboxScoreValue[]>(values);

	function onChange(
		evt: React.ChangeEvent<HTMLInputElement>,
		valueIndex: number
	) {
		let valueHasChanged = true;

		if (radioChoice && checkboxRefs.current.length) {
			checkboxStates.current.forEach((checkbox, idx) => {
				if (checkbox.isChecked && idx === valueIndex) {
					checkboxRefs.current[idx].checked = true;
					valueHasChanged = false;
				}
			});

			checkboxStates.current = checkboxStates.current.map((checkbox, idx) => ({
				...checkbox,
				isChecked: idx === valueIndex ? evt.target.checked : false,
			}));

			checkboxRefs.current
				.filter((_checkbox, idx) => idx !== valueIndex)
				.forEach((checkbox) => {
					checkbox.checked = false;
				});
		}

		if (valueHasChanged) {
			handleChange(evt);
		}
	}

	return (
		<fieldset
			className="flex flex-col"
			{...props}
			data-testid="checkbox-score-buttons"
		>
			<legend className="text-body mb-4">{label}</legend>
			<div className="flex justify-between w-full -mx-1">
				{values.map((value, idx) => {
					return (
						<div
							key={`${name}-${value.value}`}
							className="flex flex-grow w-full h-full px-1"
						>
							<Checkbox
								ref={(node) => node && (checkboxRefs.current[idx] = node)}
								data-testid={`checkbox-score-button-${name}`}
								className="checkbox--hidden-checkbox flex flex-grow w-full h-full text-center"
								name={name}
								id={`${name}-${value.value}`}
								value={value.value}
								defaultChecked={value.isChecked}
								onChange={(evt) => onChange(evt, idx)}
								pill
							>
								{value.ariaLabel ? (
									<React.Fragment>
										<span className="sr-only">{value.ariaLabel}</span>
										<span aria-hidden="true">{value.label}</span>
									</React.Fragment>
								) : (
									<React.Fragment>{value.label}</React.Fragment>
								)}
							</Checkbox>
						</div>
					);
				})}
			</div>
			{boundaryLabels && (
				<div className="text-caption flex justify-between mt-1">
					<div>{boundaryLabels.start}</div>
					<div>{boundaryLabels.end}</div>
				</div>
			)}
		</fieldset>
	);
};

export default CheckboxScoreButtons;
